import { Button, ButtonVariant } from '@traba/react-components'
import { theme } from '@traba/theme'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

/**
 * Layout primitives shared across Cascade demo dashboards.
 *
 * Design language: clean white cards over a soft Grey10 page background.
 * Typography drives hierarchy; chrome stays out of the way.
 *
 * Building blocks:
 * - ShellRoot · page wrapper
 * - HeroVerdict · top "headline" callout per dashboard
 * - MetricRow / MetricCard · KPI strip with hero-sized numbers + left accent
 * - InsightCallout · single-line, tinted strip surfacing the dashboard's
 *   most important fact
 * - HeroCard · larger, slightly elevated section for the dashboard's
 *   centerpiece visual
 * - SectionCard / SectionTitle / SectionSubtitle · supporting blocks
 * - DiscussWithNeoButton · standard CTA, navigates to /chat with the
 *   force-replay query params
 *
 * Charts get a refined Recharts wrapper (CHART_AXIS_PROPS, ChartTooltip)
 * and a small color palette tuned to the brand.
 */

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const ShellRoot = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${theme.space.med}px ${theme.space.sm}px ${theme.space.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.space.sm}px;
  background: ${theme.colors.Grey10};
  min-height: 100%;
`

const ShellHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.us}px;
  padding-bottom: ${theme.space.xs}px;
`

function BackArrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ShellTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.sm}px;
  flex-wrap: wrap;
`

const ShellTitle = styled.h1`
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.MidnightBlue};
  letter-spacing: -0.01em;
`

const ShellSubtitle = styled.p`
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey60};
`

type DashboardHeaderProps = {
  title: string
  subtitle: string
  rightSlot?: ReactNode
}

export function DashboardHeader({
  title,
  subtitle,
  rightSlot,
}: DashboardHeaderProps) {
  const navigate = useNavigate()
  return (
    <ShellHeader>
      <Button
        type="button"
        variant={ButtonVariant.TEXT}
        onClick={() => navigate('/dashboards')}
        slim
        style={{ alignSelf: 'flex-start' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <BackArrow />
          Back to Dashboards
        </span>
      </Button>
      <ShellTitleRow>
        <ShellTitle>{title}</ShellTitle>
        {rightSlot}
      </ShellTitleRow>
      <ShellSubtitle>{subtitle}</ShellSubtitle>
    </ShellHeader>
  )
}

// ---------------------------------------------------------------------------
// Verdict — the top "headline" callout
// ---------------------------------------------------------------------------

const VerdictRoot = styled.div<{ $tone: VerdictTone }>`
  border-radius: ${theme.border.radius};
  padding: ${theme.space.med}px ${theme.space.lg}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xxs}px;
  position: relative;
  overflow: hidden;
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return `linear-gradient(135deg, ${theme.colors.Red10} 0%, ${theme.colors.White} 90%)`
    }
    if ($tone === 'warning') {
      return `linear-gradient(135deg, ${theme.colors.Yellow10} 0%, ${theme.colors.White} 90%)`
    }
    return `linear-gradient(135deg, ${theme.colors.Violet10} 0%, ${theme.colors.White} 90%)`
  }};
  border: 1px solid
    ${({ $tone }) => {
      if ($tone === 'critical') {
        return theme.colors.Red30
      }
      if ($tone === 'warning') {
        return theme.colors.Yellow70
      }
      return theme.colors.Violet30
    }};
`

const VerdictAccent = styled.div<{ $tone: VerdictTone }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 5px;
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red60
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow70
    }
    return theme.colors.Violet60
  }};
`

const VerdictEyebrow = styled.div<{ $tone: VerdictTone }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red80
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow80
    }
    return theme.colors.Violet80
  }};
`

const VerdictHeadline = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${theme.colors.MidnightBlue};
  line-height: 1.25;
`

const VerdictSub = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  color: ${theme.colors.Grey60};
  line-height: 1.45;
  max-width: 80ch;
`

export type VerdictTone = 'critical' | 'warning' | 'neutral'

type HeroVerdictProps = {
  eyebrow: string
  headline: string
  sub: string
  tone?: VerdictTone
}

export function HeroVerdict({
  eyebrow,
  headline,
  sub,
  tone = 'neutral',
}: HeroVerdictProps) {
  return (
    <VerdictRoot $tone={tone}>
      <VerdictAccent $tone={tone} />
      <VerdictEyebrow $tone={tone}>{eyebrow}</VerdictEyebrow>
      <VerdictHeadline>{headline}</VerdictHeadline>
      <VerdictSub>{sub}</VerdictSub>
    </VerdictRoot>
  )
}

// ---------------------------------------------------------------------------
// Metric row + cards (hero-sized KPIs)
// ---------------------------------------------------------------------------

export const MetricRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.space.xs}px;
`

export type MetricTone = 'good' | 'warning' | 'critical' | 'neutral'

const MetricRoot = styled.div<{ $tone: MetricTone }>`
  background: ${theme.colors.White};
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  padding: ${theme.space.xs}px ${theme.space.sm}px ${theme.space.xs}px ${theme.space.sm}px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${({ $tone }) => {
      if ($tone === 'critical') {
        return theme.colors.Red60
      }
      if ($tone === 'warning') {
        return theme.colors.Yellow70
      }
      if ($tone === 'good') {
        return theme.colors.Green70
      }
      return theme.colors.Violet60
    }};
  }
`

const MetricLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-left: ${theme.space.xxs}px;
`

const MetricValue = styled.div<{ $tone: MetricTone }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 34px;
  font-weight: 700;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red80
    }
    return theme.colors.MidnightBlue
  }};
  letter-spacing: -0.02em;
  line-height: 1.05;
  padding-left: ${theme.space.xxs}px;
`

const MetricSub = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.Grey60};
  padding-left: ${theme.space.xxs}px;
`

type MetricCardProps = {
  label: string
  value: string
  sub?: string
  tone?: MetricTone
}

export function MetricCard({
  label,
  value,
  sub,
  tone = 'neutral',
}: MetricCardProps) {
  return (
    <MetricRoot $tone={tone}>
      <MetricLabel>{label}</MetricLabel>
      <MetricValue $tone={tone}>{value}</MetricValue>
      {sub && <MetricSub>{sub}</MetricSub>}
    </MetricRoot>
  )
}

// ---------------------------------------------------------------------------
// Insight callout — single-line tinted strip
// ---------------------------------------------------------------------------

const InsightRoot = styled.div<{ $tone: VerdictTone }>`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs}px;
  padding: ${theme.space.xs}px ${theme.space.sm}px;
  border-radius: ${theme.border.radius};
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red10
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow10
    }
    return theme.colors.Violet10
  }};
  border-left: 3px solid
    ${({ $tone }) => {
      if ($tone === 'critical') {
        return theme.colors.Red60
      }
      if ($tone === 'warning') {
        return theme.colors.Yellow70
      }
      return theme.colors.Violet60
    }};
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.MidnightBlue};
  line-height: 1.45;
`

const InsightGlyph = styled.span<{ $tone: VerdictTone }>`
  font-size: 16px;
  line-height: 1;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red60
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow80
    }
    return theme.colors.Violet60
  }};
`

type InsightCalloutProps = {
  glyph?: string
  text: ReactNode
  tone?: VerdictTone
}

export function InsightCallout({
  glyph = '●',
  text,
  tone = 'neutral',
}: InsightCalloutProps) {
  return (
    <InsightRoot $tone={tone}>
      <InsightGlyph $tone={tone}>{glyph}</InsightGlyph>
      <span>{text}</span>
    </InsightRoot>
  )
}

// ---------------------------------------------------------------------------
// Section cards (regular + hero)
// ---------------------------------------------------------------------------

export const SectionCard = styled.section`
  background: ${theme.colors.White};
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  padding: ${theme.space.sm}px ${theme.space.med}px ${theme.space.med}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs}px;
  min-width: 0;
`

/**
 * The dashboard's centerpiece block — slightly larger padding and a soft
 * top-edge gradient so it visually anchors the page. Use sparingly: one
 * per dashboard.
 */
export const HeroCard = styled.section`
  background: linear-gradient(180deg, ${theme.colors.White} 0%, ${theme.colors.White} 100%);
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  padding: ${theme.space.med}px ${theme.space.lg}px ${theme.space.lg}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.space.sm}px;
  min-width: 0;
  box-shadow: ${theme.shadow.sm};
`

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
  letter-spacing: -0.005em;
`

export const SectionSubtitle = styled.p`
  margin: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.Grey60};
  line-height: 1.45;
`

// ---------------------------------------------------------------------------
// Grid helpers
// ---------------------------------------------------------------------------

export const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.space.sm}px;

  @media (max-width: ${theme.media.maxMedium}) {
    grid-template-columns: 1fr;
  }
`

export const ThreeColRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${theme.space.sm}px;

  @media (max-width: ${theme.media.maxMedium}) {
    grid-template-columns: 1fr;
  }
`

// ---------------------------------------------------------------------------
// Inline bar viz (for table cells)
// ---------------------------------------------------------------------------

const BarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: ${theme.colors.Grey20};
  border-radius: 999px;
  overflow: hidden;
  min-width: 60px;
`

const BarFill = styled.div<{ $widthPct: number; $tone: MetricTone }>`
  height: 100%;
  width: ${({ $widthPct }) => `${Math.max(2, Math.min(100, $widthPct))}%`};
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red60
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow70
    }
    if ($tone === 'good') {
      return theme.colors.Green70
    }
    return theme.colors.Violet60
  }};
  border-radius: 999px;
  transition: width 0.3s ease;
`

const InlineBarRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xxs}px;
`

const InlineBarLabel = styled.span<{ $tone: MetricTone }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  font-weight: 600;
  min-width: 48px;
  text-align: right;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red80
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow80
    }
    if ($tone === 'good') {
      return theme.colors.Green80
    }
    return theme.colors.MidnightBlue
  }};
`

type InlineBarProps = {
  /** 0-100 width percent. */
  widthPct: number
  /** Display string shown alongside the bar (e.g. "7.2%", "+88h"). */
  label: string
  tone?: MetricTone
  /** Right-aligned (label after bar) or left-aligned (label before bar). */
  labelSide?: 'right' | 'left'
}

export function InlineBar({
  widthPct,
  label,
  tone = 'neutral',
  labelSide = 'right',
}: InlineBarProps) {
  if (labelSide === 'left') {
    return (
      <InlineBarRoot>
        <InlineBarLabel $tone={tone}>{label}</InlineBarLabel>
        <BarTrack>
          <BarFill $widthPct={widthPct} $tone={tone} />
        </BarTrack>
      </InlineBarRoot>
    )
  }
  return (
    <InlineBarRoot>
      <BarTrack>
        <BarFill $widthPct={widthPct} $tone={tone} />
      </BarTrack>
      <InlineBarLabel $tone={tone}>{label}</InlineBarLabel>
    </InlineBarRoot>
  )
}

// ---------------------------------------------------------------------------
// Discuss-with-Neo CTA
// ---------------------------------------------------------------------------

const DiscussButton = styled.button<{ $compact?: boolean }>`
  background: ${theme.colors.Violet};
  color: ${theme.colors.White};
  font-family: ${theme.typography.fontFamily};
  font-size: ${({ $compact }) => ($compact ? '12px' : '13px')};
  font-weight: 600;
  border: none;
  border-radius: ${theme.border.radius};
  padding: ${({ $compact }) => ($compact ? '6px 12px' : '10px 18px')};
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.Violet70};
    transform: translateY(-1px);
  }
`

type DiscussWithNeoProps = {
  scriptId: string
  prompt: string
  label?: string
  compact?: boolean
}

export function DiscussWithNeoButton({
  scriptId,
  prompt,
  label = 'Discuss with Neo',
  compact = false,
}: DiscussWithNeoProps) {
  const navigate = useNavigate()
  const handleClick = () => {
    const params = new URLSearchParams()
    params.set('demoScript', scriptId)
    params.set('prompt', prompt)
    navigate(`/chat?${params.toString()}`)
  }
  return (
    <DiscussButton $compact={compact} onClick={handleClick}>
      {label}
    </DiscussButton>
  )
}

/**
 * Section header with title + subtitle on the left and an optional action
 * slot on the top-right (typically a compact Discuss-with-Neo button). Use
 * this instead of bare SectionTitle/SectionSubtitle when the card has a
 * top-right CTA — keeps the rhythm consistent across cards.
 */
const SectionHeaderRoot = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.space.sm}px;
`

const SectionHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

type SectionHeaderProps = {
  title: string
  subtitle?: string
  action?: ReactNode
}

export function SectionHeader({ title, subtitle, action }: SectionHeaderProps) {
  return (
    <SectionHeaderRoot>
      <SectionHeaderText>
        <SectionTitle>{title}</SectionTitle>
        {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
      </SectionHeaderText>
      {action}
    </SectionHeaderRoot>
  )
}

// ---------------------------------------------------------------------------
// Chart theme — refined Recharts presets
// ---------------------------------------------------------------------------

export const CHART_PALETTE = {
  brand: theme.colors.Violet60,
  brandSoft: theme.colors.Violet30,
  good: theme.colors.Green70,
  warning: theme.colors.Yellow70,
  critical: theme.colors.Red60,
  neutral: theme.colors.Grey40,
  axisText: theme.colors.Grey60,
  axisLine: theme.colors.Grey20,
  gridLine: theme.colors.Grey20,
} as const

export const CHART_AXIS_PROPS = {
  tick: { fill: CHART_PALETTE.axisText, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: CHART_PALETTE.axisLine },
} as const

const TooltipBox = styled.div`
  background: ${theme.colors.White};
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  padding: ${theme.space.xs}px ${theme.space.sm}px;
  box-shadow: ${theme.shadow.md};
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.MidnightBlue};
  min-width: 140px;
`

const TooltipLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${theme.space.xxs}px;
`

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.sm}px;
  padding: 2px 0;
`

const TooltipDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`

type ChartTooltipPayload = {
  name?: string
  value: number | string
  color?: string
  unit?: string
}

type ChartTooltipProps = {
  label?: string
  payload?: ChartTooltipPayload[]
  active?: boolean
  formatter?: (value: number | string, name?: string) => string
}

/**
 * Custom Recharts tooltip content. Pass via `<Tooltip content={ChartTooltip} />`.
 * Accepts standard Recharts tooltip props plus an optional `formatter`.
 */
export function ChartTooltip({
  label,
  payload,
  active,
  formatter,
}: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null
  }
  return (
    <TooltipBox>
      {label && <TooltipLabel>{label}</TooltipLabel>}
      {payload.map((p, i) => {
        const valueText = formatter
          ? formatter(p.value, p.name)
          : `${p.value}${p.unit ?? ''}`
        return (
          <TooltipRow key={`${p.name}-${i}`}>
            <span>
              {p.color && <TooltipDot $color={p.color} />}
              {p.name ?? ''}
            </span>
            <strong>{valueText}</strong>
          </TooltipRow>
        )
      })}
    </TooltipBox>
  )
}
